import {useMutation} from '@apollo/client';
import * as React from 'react';
import {Link} from 'react-router';

import {FeedPost, acaoDeCurtida, curtidaOtimista, formatRelativeTime, postAuthorInitial, postAuthorName} from '../socialFeed';
import {MUTATION_STUDENT_LIKE} from '../studentQueries';
import {StudentTheme} from '../studentTheme';

interface Props {
  post: FeedPost;
  theme: StudentTheme;
}

export const PostCard: React.FC<Props> = ({post, theme}) => {
  const avatar = post.author?.avatar?.uri;
  const quando = formatRelativeTime(post.createdAt);

  const [erro, setErro] = React.useState<string | null>(null);
  const [curtir, {loading: curtindo}] = useMutation(MUTATION_STUDENT_LIKE);

  // O post volta com id/liked/likesTotal, entao o Apollo normaliza no cache e o
  // card se atualiza sozinho — sem recarregar o feed e perder a rolagem.
  const alternarCurtida = () => {
    const acao = acaoDeCurtida(post);

    setErro(null);
    curtir({
      variables: {postId: post.id, action: acao},
      optimisticResponse: {
        actLike: {
          __typename: 'LikesPayload',
          post: {__typename: 'Post', id: post.id, ...curtidaOtimista(post, acao)},
        },
      },
    }).catch((falha: Error) => setErro(falha.message || 'Não foi possível curtir.'));
  };

  return (
    <article className="post-card" style={{backgroundColor: theme.body, borderColor: theme.border}}>
      <header className="post-card__header">
        {avatar ? (
          <img className="post-card__avatar" src={avatar} alt="" loading="lazy" />
        ) : (
          // Sem avatar, a inicial evita um buraco no layout e um alt vazio.
          <span
            className="post-card__avatar post-card__avatar--vazio"
            style={{backgroundColor: theme.neutral, color: theme.background}}
            aria-hidden="true"
          >
            {postAuthorInitial(post)}
          </span>
        )}

        <div className="post-card__identidade">
          <span className="post-card__autor">{postAuthorName(post)}</span>
          {quando && (
            <span className="post-card__quando" style={{color: theme.neutral}}>
              {quando}
            </span>
          )}
        </div>

        {post.pinned && (
          <i className="fa-light fa-thumbtack post-card__fixado" style={{color: theme.primary}} aria-label="Fixado" />
        )}
      </header>

      {post.message && <p className="post-card__mensagem">{post.message}</p>}

      {post.image?.uri && <img className="post-card__imagem" src={post.image.uri} alt="" loading="lazy" />}

      <footer className="post-card__rodape" style={{color: theme.neutral}}>
        <button
          type="button"
          className="post-card__metrica post-card__acao"
          style={{color: post.liked ? theme.primary : theme.text}}
          onClick={alternarCurtida}
          disabled={curtindo}
          aria-pressed={Boolean(post.liked)}
          aria-label={post.liked ? 'Descurtir' : 'Curtir'}
        >
          <i className={`${post.liked ? 'fa-solid' : 'fa-light'} fa-heart`} aria-hidden="true" />
          {post.likesTotal ?? 0}
        </button>
        <Link
          to={`/post/${post.id}`}
          className="post-card__metrica post-card__acao"
          style={{color: theme.text}}
          aria-label="Ver comentários"
        >
          <i className="fa-light fa-comment" aria-hidden="true" />
          {post.comments?.total ?? 0}
        </Link>
      </footer>

      {erro && (
        <p className="post-card__erro" style={{color: theme.neutral}} role="alert">
          {erro}
        </p>
      )}
    </article>
  );
};
